/**
 * unitLocate — resolve every VISUAL location a unit has across the project
 * so the "Locate" action can reliably open the right scene and highlight it.
 *
 * Sources (in priority order):
 *   1. Polygon zones      (structure.zones where entity_id === unit.id)
 *   2. Facade grid cells  (structure.facade_grid_layers[].cell_assignments)
 *   3. Linked/360 scene   (scene.panorama_parent_unit_id === unit.id)
 *   4. Floor / stack view (unit has a floor + its block opts into floor view)
 *
 * Each returned location: { type, sceneId|null, sceneName|null, label, ...extra }
 */
export function readFloor(e) {
  if (e?.floor != null && e.floor !== "") {
    const n = Number(e.floor);
    if (Number.isFinite(n)) return n;
  }
  const f = e?.fields?.Floor ?? e?.fields?.floor;
  if (f == null || f === "") return null;
  const n = Number(f);
  return Number.isFinite(n) ? n : null;
}

export function resolveUnitLocations(unit, structure, inventory) {
  if (!unit || !structure) return [];
  const scenesById = new Map((structure.scenes || []).map((s) => [s.id, s]));
  const sceneName = (id) => scenesById.get(id)?.name || "Scene";
  const out = [];
  const seen = new Set();
  const push = (type, sceneId, extra = {}) => {
    const key = `${type}:${sceneId || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ type, sceneId: sceneId || null, sceneName: sceneId ? sceneName(sceneId) : null, ...extra });
  };

  // 1. Polygon zones
  for (const z of structure.zones || []) {
    if (z.entity_id === unit.id && z.scene_id) push("zone", z.scene_id, { label: sceneName(z.scene_id) });
  }
  // 2. Facade grid cell assignments
  for (const layer of structure.facade_grid_layers || []) {
    const cells = layer.cell_assignments || [];
    const hit = cells.some((c) => c.unit_id === unit.id || (c.unit_code && unit.code && c.unit_code === unit.code));
    if (hit && layer.scene_id) push("facade", layer.scene_id, { label: `${sceneName(layer.scene_id)} · Facade` });
  }
  // 3. Linked / 360 scene
  for (const s of structure.scenes || []) {
    if (s.panorama_parent_unit_id === unit.id) push("scene", s.id, { label: `${s.name || "Scene"} · 360°` });
  }
  // 4. Floor / stack view
  const floor = readFloor(unit);
  if (floor != null) {
    const block = (inventory?.groups || []).find((g) => g.id === unit.group_id && g.group_type === "block");
    const mode = block?.selection_mode;
    if (block && mode !== "none" && mode !== "facade_grid") {
      push("floor", null, { label: `Floor view · Floor ${floor}`, floor, blockId: unit.group_id });
    }
  }
  return out;
}

export const LOCATE_TYPE_LABEL = {
  zone: "Building view",
  facade: "Facade view",
  scene: "360° tour",
  floor: "Floor plan view",
};
